import React, { useState } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import PropTypes from "prop-types";
import {
	Container,
	ContentGridWrapper,
	ImageContainer,
	TextContainer,
	ItemsWrapper,
	LabelContainer,
	VideoWrapper,
	ColHeadingWrapper,
	ColButtonWrapper,
	TextHeadline,
} from "./styles";
import {
	Grid,
	Col,
	Text,
	Link,
	Badge,
	CloudinaryImage,
	VideoPlayer,
	Slider,
	Modal,
	VideoElement,
	Button,
} from "@website-builder/ui/shared/elements";
import { handleCTAClick } from "@website-builder/utilities/utils/DesignUtils.js";
import { TABLET_M_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";

export const ContentGrid = ({
	eyebrowLabel,
	headline,
	gridItems,
	cta,
	ctaPosition,
	carouselViewCount,
	hasDarkBackground,
	...props
}) => {
	const isMobile = useMediaQuery(`(max-width: ${TABLET_M_BP}px)`);
	const [openVideoModal, setOpenVideoModal] = useState(false);
	const [currentVideoMedia, setCurrentVideoMedia] = useState("");

	const sliderSettings = {
		dots: true,
		infinite: true,
		slidesToShow: carouselViewCount,
		slidesToScroll: carouselViewCount,
		speed: 500,
		arrows: true,
	};

	const whiteText = hasDarkBackground ? "text-color-white" : "";
	const isDesktopViewCarousel = gridItems?.length > 5;

	const renderMedia = (media, linkProps = {}) => {
		if (!media?.image) {
			return (
				<VideoWrapper>
					<VideoPlayer
						onClick={(mediaUrl) => {
							setCurrentVideoMedia(mediaUrl);
							setOpenVideoModal(true);
						}}
						className="content-grid-item-video"
						haveModal={false}
						videoImageUrl={media?.videoImage?.image}
						altTag={media?.videoImage?.altTag}
						mediaUrl={media?.videoUrl?.url}
					/>
				</VideoWrapper>
			);
		}
		return (
			<ImageContainer {...linkProps}>
				<CloudinaryImage
					className="content-grid-item-image"
					url={media?.image?.image}
					quality="auto"
					lazyload
					fetchFormat="auto"
					dynamic
				/>
			</ImageContainer>
		);
	};

	const renderTextContent = (item, linkProps) => (
		<TextContainer>
			{item?.badgeText || item?.badgeAdjacentText ? (
				<LabelContainer>
					{item?.badgeText ? (
						<Badge
							textColor={"0E5642"}
							bgColor={"#C3F570"}
							text={item?.badgeText}
							className="content-grid-item-badge"
						/>
					) : null}
					{item?.badgeAdjacentText ? (
						<Text className={`text-color-gray ${whiteText}`} variant="label_M">
							{item?.badgeAdjacentText}
						</Text>
					) : null}
				</LabelContainer>
			) : null}
			{item?.subText ? <Text className={whiteText}>{item.subText}</Text> : null}
			{item?.title ? (
				<Text
					className={whiteText}
					variant="headline_6"
					{...(item?.isTitleClickable ? linkProps : {})}
				>
					{item.title}
				</Text>
			) : null}
			{item?.description ? (
				<Text className={whiteText} variant="paragraph_M">
					{item.description}
				</Text>
			) : null}
			{item?.linkText ? (
				<Link
					className="content-grid-item-link"
					{...linkProps}
					arrowCta
					linkText={item?.linkText}
					darkBg={hasDarkBackground}
				/>
			) : null}
		</TextContainer>
	);

	const renderGridItems = () => {
		return gridItems?.map((item, index) => {
			const linkProps = {
				as: "a",
				href: item.link?.link,
				target: item.link?.target,
			};
			return (
				<Container key={`item-${index}`}>
					{item?.media
						? renderMedia.apply(
								this,
								item?.isImageClickable
									? [item?.media, linkProps]
									: [item?.media],
							)
						: null}
					{renderTextContent(item, linkProps)}
				</Container>
			);
		});
	};

	const handleButtonClick = (cta) => {
		let eventData = {
			page_section: props?.pageSection,
			click_text: cta?.buttonText,
			redirect_to: cta?.link?.link,
		};
		handleCTAClick(cta, eventData);
	};

	return (
		<ContentGridWrapper className="content-grid-module">
			<Grid>
				{eyebrowLabel || headline ? (
					<ColHeadingWrapper
						span={12}
						mdSpan={6}
						isDesktopViewCarousel={isDesktopViewCarousel}
					>
						<div>
							{eyebrowLabel ? (
								<Text className={whiteText} variant="label_M">
									{eyebrowLabel}
								</Text>
							) : null}
							{headline ? (
								<TextHeadline className={whiteText} variant="headline_3">
									{headline}
								</TextHeadline>
							) : null}
						</div>
						{cta && !isMobile && ctaPosition === "heading" ? (
							<Button
								buttonText={cta?.buttonText}
								type={cta?.type}
								darkBg={cta?.darkBg}
								onClick={() => handleButtonClick(cta)}
								className="content-grid-heading-cta"
							/>
						) : null}
					</ColHeadingWrapper>
				) : null}

				<Col span={12} mdSpan={6}>
					{isDesktopViewCarousel || isMobile ? (
						<Slider
							className="content-grid-slider"
							otherSettings={{
								...sliderSettings,
								dotsColor: false
									? "var(--primary-green-400)"
									: "var(--primary-forest-400)",
							}}
							carouselData={renderGridItems()}
							darkBg={hasDarkBackground}
						/>
					) : (
						<ItemsWrapper>{renderGridItems()}</ItemsWrapper>
					)}
				</Col>
				{cta && (isMobile || ctaPosition === "grid") ? (
					<ColButtonWrapper span={12} mdSpan={6}>
						<Button
							buttonText={cta?.buttonText}
							type={cta?.type}
							darkBg={cta?.darkBg}
							onClick={() => handleButtonClick(cta)}
						/>
					</ColButtonWrapper>
				) : null}
			</Grid>
			<Modal
				className="content-grid-video-modal"
				show={openVideoModal}
				disableBackdropClick={true}
				onClose={() => setOpenVideoModal(false)}
				media
			>
				{openVideoModal && <VideoElement url={currentVideoMedia} />}
			</Modal>
		</ContentGridWrapper>
	);
};

export default ContentGrid;

ContentGrid.propTypes = {
	eyebrowLabel: PropTypes.string,
	headline: PropTypes.string,
	gridItems: PropTypes.arrayOf(
		PropTypes.shape({
			media: PropTypes.object,
			isImageClickable: PropTypes.bool,
			badgeText: PropTypes.string,
			badgeAdjacentText: PropTypes.string,
			subText: PropTypes.string,
			title: PropTypes.string,
			isTitleClickable: PropTypes.bool,
			description: PropTypes.string,
			linkText: PropTypes.string,
			link: PropTypes.shape({
				link: PropTypes.string.isRequired,
				target: PropTypes.string,
			}),
		}),
	).isRequired,
	cta: PropTypes.shape({
		buttonText: PropTypes.string.isRequired,
		type: PropTypes.string,
		darkBg: PropTypes.bool,
		link: PropTypes.shape({
			link: PropTypes.string,
			target: PropTypes.string,
		}),
	}),
	ctaPosition: PropTypes.oneOf(["heading", "grid"]),
	carouselViewCount: PropTypes.number,
	hasDarkBackground: PropTypes.bool,
};

ContentGrid.defaultProps = {
	ctaPosition: "heading",
	carouselViewCount: 3,
	hasDarkBackground: false,
};
